import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Shield, Loader2, RefreshCw } from 'lucide-react';
import TrustTradeLogo from '../components/TrustTradeLogo';
import MoneyBreakdown from '../components/MoneyBreakdown';
import { Product as D } from '../components/productTokens';
import api from '../utils/api';
import { usePlatformConfig } from '../context/PlatformConfigContext';
import {
  getPaymentResultCopy,
  isProviderFundingConfirmed,
  isTrustedCheckoutMessage,
} from '../components/transactionState';

const METHOD_LABELS = {
  CARD: 'Card',
  OZOW: 'Ozow Instant EFT',
  EFT: 'EFT',
};

// Payment-stage-only exception: TrustTrade otherwise never names its payment
// provider in general app copy (see paymentFeeConsistency.test.js's "FAQ does
// not publicly name TradeSafe" and transactionState.test.js's TradeSafe-free
// assertions). Here, where the buyer is about to enter real payment details
// into a cross-origin hosted iframe (confirmed: checkout_url/payment_link
// resolve to TradeSafe's own pay.tradesafe.co.za domain, not a TrustTrade
// origin), naming the real provider is what makes the handoff look legitimate
// rather than anonymous. Do not reuse this constant outside this screen.
const PAYMENT_PROVIDER_NAME = 'TradeSafe';

function rands(value) {
  return `R ${Number(value).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function payerLabel(value) {
  if (value === 'seller') return 'Paid by the seller';
  if (value === 'split') return 'Split between the buyer and the seller';
  if (value === 'agent') return 'Paid by TrustTrade';
  return 'Paid by the buyer';
}

const QUOTE_MAX_AGE_MS = 10 * 60 * 1000;

export function quoteIsFresh(data, now = Date.now()) {
  const quotedAt = Date.parse(data?.quoted_at || '');
  if (!Number.isFinite(quotedAt)) return false;
  const age = now - quotedAt;
  return age <= QUOTE_MAX_AGE_MS && age >= -60 * 1000;
}

export function moneyCents(value) {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(String(value).trim());
  if (!Number.isFinite(n)) return null;
  const text = n.toFixed(4);
  const negative = text.startsWith('-');
  const [whole, frac] = text.replace('-', '').split('.');
  let cents = Number(whole) * 100 + Number(frac.slice(0, 2));
  if (frac[2] >= '5') cents += 1;
  return negative ? -cents : cents;
}

export function checkoutTotalConflicts(quotedBuyerPays, checkoutTotal) {
  const quoted = moneyCents(quotedBuyerPays);
  const charged = moneyCents(checkoutTotal);
  if (quoted == null || charged == null) return false;
  return quoted !== charged;
}

function quoteFailureCopy(data) {
  if (data?.reason === 'fee_basis_mismatch') {
    return 'The TrustTrade fee stored for this payment does not match the 2% fee used for the TradeSafe quote, so the payable total cannot be confirmed. Checkout is blocked. No payment was started.';
  }
  if (data?.reason === 'buyer_total_mismatch') {
    return 'The TradeSafe buyer total does not match the amount this screen would show, so checkout is blocked. No payment was started.';
  }
  if (!data || data.reason === 'provider_unavailable' || data.reason === 'quote_unavailable' || data.checkout_continues === true) {
    return 'TradeSafe did not return a price quote. Checkout is blocked until a quote succeeds. No estimated fee is shown and no payment was started.';
  }
  return 'A confirmed price quote is required before payment. No estimated amount is shown and no payment was started.';
}

function quoteErrorMessage(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  const text = typeof detail === 'string' ? detail : (detail?.message || '');
  if (err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '')) {
    return 'The price quote timed out. Checkout is blocked so an unconfirmed amount is not shown. No payment was started.';
  }
  if (!err?.response) {
    return 'The price quote could not be reached. Checkout is blocked. No payment was started.';
  }
  if (status === 404 && (!text || text === 'Not Found')) {
    return 'Checkout pricing is not available from the server (Not Found). Payment was not started.';
  }
  if (status >= 500) {
    return 'The price quote failed. Checkout is blocked. No payment was started.';
  }
  if (text) return text;
  return 'A confirmed price quote is required before payment. No payment was started.';
}


export function checkoutRedirectUiStatus(searchParams) {
  const status = String(searchParams?.get?.('status') || '').toLowerCase();
  if (!status) return null;
  const reason = String(searchParams?.get?.('reason') || '').toLowerCase();
  const canceled = reason === 'canceled' || reason === 'cancelled';
  if (canceled && (status === 'failure' || status === 'failed' || status === 'error')) {
    return 'canceled';
  }
  return status;
}

export default function PaymentCheckout() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const transactionId = searchParams.get('tx') || searchParams.get('transaction_id') || searchParams.get('reference');
  const dealId = searchParams.get('deal');
  const milestoneId = searchParams.get('milestone');
  const method = (searchParams.get('method') || '').toLowerCase();
  const redirectStatus = checkoutRedirectUiStatus(searchParams);

  const [checkout, setCheckout] = useState(null);
  const [record, setRecord] = useState(null);
  const [uiEvent, setUiEvent] = useState(redirectStatus ? { status: redirectStatus } : null);
  const [loading, setLoading] = useState(!redirectStatus);
  const [error, setError] = useState(null);
  const [feeQuote, setFeeQuote] = useState(null);
  const [feeRow, setFeeRow] = useState(null);
  const [awaitingConfirm, setAwaitingConfirm] = useState(false);
  const [quoteBlocked, setQuoteBlocked] = useState(null);

  const returnPath = dealId ? `/smart-deals/${dealId}` : (transactionId ? `/transactions/${transactionId}` : '/dashboard');
  const iframeRef = useRef(null);
  const funded = isProviderFundingConfirmed(record);
  const resultCopy = getPaymentResultCopy({ uiEvent, transaction: record });
  const showResult = Boolean(uiEvent) || funded;
  const payHeading = funded ? 'Payment Protected' : 'Complete Payment';

  const loadRecord = useCallback(async () => {
    try {
      if (dealId && milestoneId) {
        // A milestone's own funding state — NOT the parent deal's. The parent
        // deal's tradesafe_id/tradesafe_state describe the deal as a whole, so
        // isProviderFundingConfirmed(parentDeal) can read as funded/unfunded
        // based on a DIFFERENT milestone than the one actually being paid here.
        const res = await api.get(`/smart-deals/${dealId}/milestones/${milestoneId}/funding-status`, { withCredentials: true });
        setRecord(res.data);
        return res.data;
      }
      if (dealId) {
        const res = await api.get(`/smart-deals/${dealId}`, { withCredentials: true });
        setRecord(res.data);
        return res.data;
      }
      if (transactionId) {
        const res = await api.get(`/transactions/${transactionId}`, { withCredentials: true });
        setRecord(res.data);
        return res.data;
      }
    } catch {
      // Unauthenticated return from the provider: keep verification copy.
    }
    return null;
  }, [dealId, milestoneId, transactionId]);

  const quoteRequest = useRef(0);

  const blockQuote = useCallback((message, testId = 'quote-blocked') => {
    setQuoteBlocked({ message, testId });
    setFeeQuote(null);
    setFeeRow(null);
    setAwaitingConfirm(false);
    setCheckout(null);
    setError(null);
    setLoading(false);
  }, []);

  const startCheckout = useCallback(async () => {
    if (!transactionId && !dealId) {
      setError('Missing transaction.');
      setLoading(false);
      return;
    }
    if (!feeQuote?.quote_id || feeRow?.buyer_pays == null) {
      blockQuote('This checkout has no approved TradeSafe quote. Refresh the quote before paying. No payment was started.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      let data;
      const paymentBody = {
        payment_method: method || 'card',
        quote_id: feeQuote.quote_id,
        approved_total: feeRow.buyer_pays,
      };
      if (dealId && milestoneId) {
        const res = await api.post(`/smart-deals/${dealId}/milestones/${milestoneId}/fund`, paymentBody, { withCredentials: true });
        data = res.data;
      } else if (dealId) {
        const res = await api.post(`/smart-deals/${dealId}/fund`, paymentBody, { withCredentials: true });
        data = res.data;
      } else {
        const params = new URLSearchParams({
          payment_method: method || 'card',
          quote_id: feeQuote.quote_id,
          approved_total: String(feeRow.buyer_pays),
        });
        const res = await api.get(`/tradesafe/payment-url/${transactionId}?${params.toString()}`, { withCredentials: true });
        data = res.data;
      }
      if (data.already_paid) {
        await loadRecord();
        setUiEvent({ status: 'success' });
        return;
      }
      if (data.verification_required) {
        setUiEvent({ status: 'unknown', source: 'backend' });
        return;
      }
      if (data.eft_details && !data.payment_link) {
        navigate(returnPath, { replace: true });
        return;
      }
      if (checkoutTotalConflicts(feeRow?.buyer_pays, data?.total_value)) {
        blockQuote(
          `Secure checkout returned ${rands(data.total_value)}, which does not match the approved quote of ${rands(feeRow.buyer_pays)}. Payment is blocked so the quoted total is not replaced. No payment was started.`,
          'quote-total-mismatch',
        );
        return;
      }
      setCheckout(data);
    } catch (err) {
      setError(err.response?.data?.detail?.message || err.response?.data?.detail || err.message || 'Unable to start payment.');
    } finally {
      setLoading(false);
    }
  }, [blockQuote, dealId, feeQuote, feeRow, milestoneId, method, transactionId, loadRecord, navigate, returnPath]);

  const prepareCheckout = useCallback(async () => {
    if (!transactionId && !dealId) {
      setError('Missing transaction.');
      setLoading(false);
      return;
    }
    const requestId = ++quoteRequest.current;
    const current = () => requestId === quoteRequest.current;
    setLoading(true);
    setError(null);
    setQuoteBlocked(null);
    setAwaitingConfirm(false);
    setCheckout(null);
    setFeeQuote(null);
    setFeeRow(null);
    const body = { payment_method: (method || 'card').toUpperCase() };
    if (transactionId) body.transaction_id = transactionId;
    if (dealId) body.deal_id = dealId;
    if (milestoneId) body.milestone_id = milestoneId;
    try {
      const quoteRes = await api.post('/tradesafe/checkout-quote', body, { withCredentials: true });
      if (!current()) return;
      const data = quoteRes?.data;
      const row = (data?.methods || []).find(item => item.method === body.payment_method);
      const complete = Boolean(
        data?.quoted && row?.available && row.tradesafe_fee != null
        && row.buyer_pays != null && row.seller_receives != null
      );
      if (complete && quoteIsFresh(data) && !data.quote_id) {
        blockQuote('The server did not attach an approved quote reference, so payment cannot start. No payment was started.');
        return;
      }
      if (complete && quoteIsFresh(data)) {
        setFeeQuote(data);
        setFeeRow(row);
        setAwaitingConfirm(true);
        setLoading(false);
        return;
      }
      if (complete) {
        blockQuote('This price quote is out of date. Refresh it before paying. No payment was started.');
        return;
      }
      if (data?.reason === 'not_quoted' || row?.available === false) {
        blockQuote(
          `TradeSafe did not quote a fee for ${METHOD_LABELS[body.payment_method] || body.payment_method}, so this checkout cannot continue. Choose another payment method, or retry. No estimated amount is shown and no payment was started.`,
          'fee-not-quoted',
        );
        return;
      }
      blockQuote(quoteFailureCopy(data));
    } catch (err) {
      if (!current()) return;
      blockQuote(quoteErrorMessage(err));
    }
  }, [blockQuote, dealId, method, milestoneId, transactionId]);

  useEffect(() => {
    loadRecord();
  }, [loadRecord]);

  useEffect(() => {
    if (showResult) return undefined;
    prepareCheckout();
    return undefined;
  }, [showResult, prepareCheckout]);

  // Poll the durable backend record as soon as a checkout session exists,
  // independent of showResult. TradeSafe's checkoutLink has no per-transaction
  // redirect and completion is otherwise only signalled via postMessage — if a
  // 3-D Secure step breaks out of the iframe, or the postMessage origin check
  // ever misses a legitimate message, this is the only thing that still
  // detects a real payment and moves the UI to "Payment Protected". Without
  // it the page can get stuck showing pre-payment estimates (or dashes)
  // forever after a payment has actually succeeded.
  useEffect(() => {
    if (funded || !checkout) return undefined;
    const id = setInterval(() => {
      loadRecord();
    }, 2500);
    return () => clearInterval(id);
  }, [checkout, funded, loadRecord]);

  useEffect(() => {
    const onMessage = (event) => {
      const expectedSource = iframeRef.current ? iframeRef.current.contentWindow : undefined;
      if (!isTrustedCheckoutMessage(event, {
        expectedSource,
        transactionId,
        dealId,
        milestoneId,
      })) return;
      const status = String(event.data?.status || '').toLowerCase();
      setUiEvent({ status, source: 'postMessage' });
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [transactionId, dealId, milestoneId]);

  const { config: platformConfig } = usePlatformConfig();
  const methodLabel = METHOD_LABELS[(checkout?.method || method || '').toUpperCase()] || (checkout?.method || method || 'Card');
  const itemLabel = checkout?.item_description || record?.item_description || record?.title || 'TrustTrade transaction';
  const itemAmount = feeQuote?.item_amount ?? checkout?.item_price ?? record?.item_price ?? record?.amount;
  const platformFee = feeQuote?.trusttrade_fee ?? checkout?.platform_fee ?? record?.platform_fee ?? record?.trusttrade_fee;
  const platformFeePercent = platformConfig?.platform_fee_percent;
  // Hosted Card and Ozow use checkoutLink, which returns a URL and no deposit
  // total. total_value / processing_fee appear only when a deposit was created
  // (the EFT path). total_value is the only field comparable to the approved
  // buyer total. processing_fee, fee_breakdown, item_price and platform_fee
  // are not that total. An approved quote is never replaced by a later number.
  const exactProcessing = checkout?.processing_fee;
  const exactTotal = checkout?.total_value;
  const quotedProcessing = feeRow?.tradesafe_fee;
  const quotedTotal = feeRow?.buyer_pays;
  const totalIsEstimate = exactTotal == null && quotedTotal == null;
  const processing = quotedProcessing != null ? quotedProcessing : (exactProcessing != null ? exactProcessing : null);
  const total = quotedTotal != null ? quotedTotal : (exactTotal != null ? exactTotal : null);
  const sellerReceives = feeRow ? feeRow.seller_receives : record?.seller_receives;
  const sellerReceivesIsCeiling = feeRow
    ? false
    : Boolean(record?.fee_allocation && record.fee_allocation !== 'BUYER' && !record.seller_payout_provider_confirmed);
  const hostedTotalConflicts = checkoutTotalConflicts(quotedTotal, exactTotal);
  const showQuotedBreakdown = !quoteBlocked && !totalIsEstimate;
  const subtotalBeforeProvider = (itemAmount != null && platformFee != null)
    ? Number(itemAmount) + Number(platformFee)
    : null;
  // Already available on the record/URL — no new backend field needed.
  const reference = record?.share_code || transactionId || dealId || null;

  const iframeSrc = useMemo(() => checkout?.checkout_url || checkout?.payment_link, [checkout]);
  // "Card details"/"banking details" are only accurate for the method actually
  // in use; a generic fallback covers the brief instant before checkout.method
  // resolves. Still verified true for both: the hosted checkoutLink iframe is
  // cross-origin to TradeSafe's own domain and no backend route accepts a
  // card/CVV/bank-login field, so TrustTrade never receives either.
  const privacyDetailNoun = methodLabel === 'Card'
    ? 'card details'
    : methodLabel.startsWith('Ozow')
      ? 'banking details'
      : 'payment details';

  return (
    <div className="tt-checkout" style={{ minHeight: '100vh', background: D.bg, backgroundImage: D.bgGlow, color: D.text, padding: '20px 16px 40px' }}>
      <div style={{ maxWidth: 880, margin: '0 auto' }}>
        {showResult ? (
          <div>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}><TrustTradeLogo dark size="medium" /></div>
            <div data-testid="payment-result" style={{ background: D.surfaceSolid, border: `1px solid ${D.border}`, borderRadius: 16, padding: 32, textAlign: 'center', boxShadow: D.shadow }}>
            <h1 data-testid="payment-result-title" style={{ fontSize: 24, margin: '0 0 10px', color: D.text }}>{resultCopy.title}</h1>
            <p style={{ color: D.sub, lineHeight: 1.6 }}>{resultCopy.body}</p>
            <p style={{ fontSize: 13, color: D.dim, marginTop: 16 }}>{resultCopy.disclosure}</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 22, flexWrap: 'wrap' }}>
              {resultCopy.canRetry && (
                <button
                  type="button"
                  data-testid="retry-payment-btn"
                  onClick={() => {
                    setUiEvent(null);
                    setCheckout(null);
                    prepareCheckout();
                  }}
                  style={{ background: D.accentGradient, color: '#061421', border: 'none', borderRadius: 10, padding: '11px 16px', fontWeight: 700, cursor: 'pointer', boxShadow: D.shadow }}
                >
                  Try again
                </button>
              )}
              <button
                type="button"
                onClick={() => navigate(returnPath)}
                style={{ background: D.hover, color: D.text, border: `1px solid ${D.border}`, borderRadius: 10, padding: '11px 16px', fontWeight: 600, cursor: 'pointer' }}
              >
                View transaction
              </button>
            </div>
            </div>
          </div>
        ) : (
          <>
            {/* PAYMENT-FIRST HIERARCHY (2026-10-14): compact header -> final-
                amount strip -> the hosted TradeSafe form (the dominant
                element) -> collapsed payment breakdown -> one consolidated
                trust section. All one connected surface (.tt-checkout-card)
                so the buyer reaches the actual payment fields almost
                immediately, instead of scrolling through a full breakdown
                and several explanatory paragraphs first. */}
            <section className="tt-checkout-card" style={{ overflow: 'hidden' }} aria-label="Secure checkout">
              <header className="tt-checkout-header-compact" data-testid="provider-identity">
                <TrustTradeLogo dark size="small" />
                <div className="tt-checkout-header-text">
                  <p className="tt-eyebrow">Secure checkout</p>
                  <h1>Payment processed securely by {PAYMENT_PROVIDER_NAME}</h1>
                </div>
                <div className="tt-checkout-header-meta">
                  <span data-testid="pay-chrome-label" className="tt-checkout-chrome-label">{payHeading}</span>
                  <span>Transaction {reference || 'pending'}</span>
                  <span className="tt-checkout-method">{methodLabel}</span>
                </div>
              </header>

              <div className="tt-checkout-amount-strip">
                <Shield size={14} aria-hidden="true" />
                {iframeSrc ? (
                  <p><strong>Check the final amount.</strong> The amount shown on the TradeSafe Pay button below is the exact final amount you will pay.</p>
                ) : (
                  <p><strong>Review the full amount before paying.</strong> Checkout stays on TrustTrade until a TradeSafe quote confirms what you will pay.</p>
                )}
              </div>

              {awaitingConfirm && feeQuote && feeRow && (
                <div data-testid="checkout-fee-quote" style={{ padding: '18px 20px', borderBottom: `1px solid ${D.border}` }}>
                  <p style={{ margin: '0 0 12px', fontWeight: 700 }}>Review these fees before continuing</p>
                  <div role="group" aria-label="Payment method" style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                    {['card', 'ozow'].map((item) => (
                      <button
                        key={item}
                        type="button"
                        data-testid={`quote-method-${item}`}
                        aria-pressed={(method || 'card') === item}
                        onClick={() => {
                          const next = new URLSearchParams(searchParams);
                          next.set('method', item);
                          setSearchParams(next, { replace: true });
                        }}
                        style={{ border: `1px solid ${D.border}`, borderRadius: 8, padding: '8px 12px', background: (method || 'card') === item ? D.hover : 'transparent', color: D.text, fontWeight: 700, cursor: 'pointer' }}
                      >
                        {METHOD_LABELS[item.toUpperCase()]}
                      </button>
                    ))}
                  </div>
                  {Number(feeQuote.courier_fee || 0) > 0 && <p style={{ margin: '4px 0' }}>Item price {rands(feeQuote.item_price)} plus delivery (courier) {rands(feeQuote.courier_fee)}</p>}
                  <p style={{ margin: '4px 0' }}>Transaction amount {rands(feeQuote.item_amount)}</p>
                  <p style={{ margin: '4px 0' }}>TrustTrade fee {rands(feeQuote.trusttrade_fee)}. {payerLabel(feeQuote.trusttrade_fee_paid_by)}.</p>
                  <p style={{ margin: '4px 0' }}>TradeSafe fee {rands(feeRow.tradesafe_fee)}. {payerLabel(feeRow.tradesafe_fee_paid_by)}.</p>
                  {feeRow.tradesafe_fee_excl_vat != null && <p style={{ margin: '4px 0' }}>TradeSafe fee before VAT {rands(feeRow.tradesafe_fee_excl_vat)}.</p>}
                  {feeRow.tradesafe_vat != null && <p style={{ margin: '4px 0' }}>VAT {rands(feeRow.tradesafe_vat)}.</p>}
                  <p style={{ margin: '4px 0', fontSize: 13, color: D.sub }}>
                    {feeRow.tradesafe_vat != null || feeRow.tradesafe_fee_excl_vat != null
                      ? 'The TradeSafe fee is the confirmed provider total. This quote does not itemise a separate settlement charge.'
                      : 'The TradeSafe fee is the confirmed provider total for this method. VAT and any settlement charge are not itemised separately in this quote.'}
                  </p>
                  <p style={{ margin: '4px 0', fontWeight: 700 }}>You pay {rands(feeRow.buyer_pays)}</p>
                  <p style={{ margin: '4px 0' }}>Seller&apos;s expected proceeds {rands(feeRow.seller_receives)}</p>
                  <p style={{ margin: '12px 0', fontSize: 13, color: D.sub }}>These figures come from TradeSafe&apos;s calculator for this account. Continue only if they match what you expect to pay.</p>
                  <button
                    type="button"
                    data-testid="confirm-fee-continue"
                    onClick={() => {
                      if (!quoteIsFresh(feeQuote)) {
                        blockQuote('This price quote is out of date. Refresh it before paying. No payment was started.');
                        return;
                      }
                      setAwaitingConfirm(false);
                      startCheckout();
                    }}
                    style={{ background: D.accentGradient, color: '#061421', border: 'none', borderRadius: 10, padding: '11px 16px', fontWeight: 700, cursor: 'pointer' }}
                  >
                    Continue to secure checkout
                  </button>
                </div>
              )}
              {quoteBlocked && (
                <div role="alert" data-testid={quoteBlocked.testId} className="tt-checkout-error">
                  <p style={{ margin: '0 0 12px' }}>{quoteBlocked.message}</p>
                  <button type="button" data-testid="retry-quote-btn" onClick={() => prepareCheckout()} className="tt-button"><RefreshCw size={14} /> Retry</button>
                </div>
              )}

              {loading && <div className="tt-checkout-loading" role="status"><Loader2 size={22} style={{ animation: 'spin 0.8s linear infinite' }} /><p>{awaitingConfirm || feeRow ? 'Opening secure checkout…' : 'Checking the confirmed amount…'}</p></div>}
              {error && <div className="tt-checkout-error" role="alert">{typeof error === 'string' ? error : 'Unable to start payment.'}</div>}
              {!loading && !awaitingConfirm && !quoteBlocked && !hostedTotalConflicts && iframeSrc && <iframe
                    ref={iframeRef}
                    title="TrustTrade checkout"
                    data-testid="embedded-checkout"
                    src={iframeSrc}
                    style={{ display: 'block', width: '100%', minHeight: 760, border: 'none', background: '#16181B' }}
                  />}
              {!loading && !awaitingConfirm && !quoteBlocked && !iframeSrc && !error && <div className="tt-checkout-loading"><button type="button" onClick={() => prepareCheckout()} className="tt-button"><RefreshCw size={14} /> Retry</button></div>}

              {/* Collapsed by default. Shown only once a quote or a provider
                  deposit total exists, so a guessed seller payout is not on screen. */}
              {showQuotedBreakdown && <details className="tt-checkout-breakdown-toggle">
                <summary>View payment breakdown</summary>
                <div className="tt-checkout-breakdown-body">
                  <div className="tt-checkout-context"><span>{itemLabel}</span></div>
                  <MoneyBreakdown compact itemValue={itemAmount} buyerFee={platformFee} beforeProvider={subtotalBeforeProvider} providerFee={processing} providerFeeCopy="Confirmed at secure checkout" confirmedTotal={total} sellerReceives={sellerReceives} sellerReceivesIsCeiling={sellerReceivesIsCeiling} platformFeePercent={platformFeePercent} />
                  <p className="tt-checkout-fee-note">{feeRow ? 'These figures are the TradeSafe quote for the selected payment method. The Pay button must match the buyer total before you pay.' : 'TrustTrade calculates its fee here. The payment provider confirms its processing fee and your exact final payment amount in the secure checkout before you pay.'}</p>
                </div>
              </details>}

              {/* ONE consolidated trust section — replaces the previously
                  scattered fee-note/privacy/custody paragraphs. */}
              <div className="tt-checkout-trust">
                <p className="tt-checkout-trust-heading">Secure payment</p>
                <ul>
                  <li>Payment is processed through {PAYMENT_PROVIDER_NAME}.</li>
                  <li>{methodLabel.startsWith('Ozow') ? 'TrustTrade does not receive or store your bank login details.' : <>Your {privacyDetailNoun} are entered directly into {PAYMENT_PROVIDER_NAME}&rsquo;s secure checkout and are not stored by TrustTrade.</>}</li>
                  <li>Only continue if the transaction reference and final amount are correct.</li>
                </ul>
              </div>
            </section>
            <div className="tt-checkout-return"><button type="button" data-testid="cancel-checkout-btn" onClick={() => navigate(returnPath)} className="tt-button tt-button-secondary">Cancel and return to transaction</button><p>No payment is confirmed until the provider records it.</p></div>
          </>
        )}
      </div>
    </div>
  );
}
